'use client'

import { useEffect, useState, useCallback } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { BellIcon, CheckCheckIcon, RefreshCcwIcon } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { NotificationItem } from '@/components/partials/notifications'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useSetReadNotifications } from '@/hooks/use-notifications'
import { formatNotification } from '@/lib/formatter/notification-formatter'
import { handleNotificationContent } from '@/lib/handler/notification-content-handler'
import { NotificationScopeEnum, NotificationTypeEnum, SortEnum } from '@/enums'
import { fetchNotifications } from '@/lib/api'
import { useNotificationsStore } from '@/store'
import { useWebSocket } from '@/hooks'
import { tOptional } from '@/lib/utils'
import { DashboardResponseType } from '@/types/dashboard'

const NotificationsDropdown = () => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { userNotifications } = useWebSocket()

  const updateDashboardNotificationCount = useCallback(
    (delta: number | null) => {
      queryClient.setQueryData<DashboardResponseType>(['dashboard'], old => {
        if (!old?.stats) {
          return old
        }
        const current = old.stats.unreadNotifications ?? 0
        return {
          ...old,
          stats: {
            ...old.stats,
            unreadNotifications: delta === null ? 0 : Math.max(0, current + delta)
          }
        }
      })
    },
    [queryClient]
  )
  const { mutate: markAsRead, isPending: isMarkingRead } = useSetReadNotifications()
  const [open, setOpen] = useState(false)
  const { unreadCount, setUnreadCount } = useNotificationsStore()

  // Completely independent query for dropdown - separate from the list
  // Uses different query key: 'dropdown-notifications' instead of 'notifications'
  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      'dropdown-notifications',
      {
        page: 1,
        size: 10,
        scopes: [NotificationScopeEnum.ADMINISTRATION],
        read: false,
        sort: SortEnum.DESC,
        sortBy: 'createdAt'
      }
    ],
    queryFn: () =>
      fetchNotifications({
        page: 1,
        size: 10,
        scopes: [NotificationScopeEnum.ADMINISTRATION],
        read: false,
        sort: SortEnum.DESC,
        sortBy: 'createdAt'
      }),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000
  })

  // Update store unreadCount when data changes
  useEffect(() => {
    if (data?.unreadTotal !== undefined) {
      setUnreadCount(data.unreadTotal)
    }
  }, [data?.unreadTotal, setUnreadCount])

  const displayCount = unreadCount > 9 ? '9+' : unreadCount.toString()

  // Refetch if there's an inconsistency: unreadCount > 0 but no items in the list
  useEffect(() => {
    if (!isLoading && data && unreadCount > 0 && data.items.length === 0) {
      void refetch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadCount, data?.items.length, isLoading])

  useEffect(() => {
    if (userNotifications.length > 0) {
      const latest = userNotifications[userNotifications.length - 1]

      if (!NotificationScopeEnum.ADMINISTRATION.includes(latest.scopes)) {
        return
      }

      queryClient.setQueryData(
        [
          'dropdown-notifications',
          {
            page: 1,
            size: 10,
            scopes: [NotificationScopeEnum.ADMINISTRATION],
            read: false,
            sort: SortEnum.DESC,
            sortBy: 'createdAt'
          }
        ],
        (oldData: any) => {
          if (!oldData) {
            return oldData
          }

          const exists = oldData.items.some((item: any) => item.id === latest.id)
          if (exists) {
            return oldData
          }

          const newItems = [latest, ...oldData.items].slice(0, 10)

          return {
            ...oldData,
            items: newItems,
            total: oldData.total + 1,
            unreadTotal: (oldData.unreadTotal || 0) + 1
          }
        }
      )

      // Increment unread notifications count in dashboard cache
      updateDashboardNotificationCount(1)

      void queryClient.invalidateQueries({
        queryKey: ['dropdown-notifications'],
        refetchType: 'none'
      })

      void queryClient.invalidateQueries({
        queryKey: ['notifications']
      })

      // Handle notification content - update UI cache based on payload
      handleNotificationContent(latest, queryClient)

      const formatted = formatNotification(latest)

      // Prepare toast options
      const toastOptions = {
        description: tOptional(t, formatted.message),
        duration: 5000,
        action: formatted.link
          ? {
              label: t('view'),
              onClick: async () => {
                markAsRead({ params: { ids: [latest.id] } })

                const now = new Date().toISOString()

                queryClient.setQueriesData({ queryKey: ['notifications'] }, (oldData: any) => {
                  if (!oldData || !oldData.items) {
                    return oldData
                  }
                  return {
                    ...oldData,
                    items: oldData.items.map((notif: any) =>
                      notif.id === latest.id ? { ...notif, readAt: now } : notif
                    )
                  }
                })

                queryClient.setQueriesData({ queryKey: ['dropdown-notifications'] }, (oldData: any) => {
                  if (!oldData || !oldData.items) {
                    return oldData
                  }
                  return {
                    ...oldData,
                    unreadTotal: Math.max(0, (oldData.unreadTotal || 0) - 1),
                    items: oldData.items.map((notif: any) =>
                      notif.id === latest.id ? { ...notif, readAt: now } : notif
                    )
                  }
                })

                // Decrement unread notifications count in dashboard cache
                updateDashboardNotificationCount(-1)

                await queryClient.invalidateQueries({
                  queryKey: ['notifications'],
                  refetchType: 'none'
                })
                await queryClient.invalidateQueries({
                  queryKey: ['dropdown-notifications'],
                  refetchType: 'none'
                })

                router.push(formatted.link!)
              }
            }
          : undefined
      }

      // Show toast with the appropriate type based on a notification type
      switch (latest.type) {
        case NotificationTypeEnum.ERROR:
          toast.error(t(formatted.title), toastOptions)
          break
        case NotificationTypeEnum.WARNING:
          toast.warning(t(formatted.title), toastOptions)
          break
        case NotificationTypeEnum.SUCCESS:
          toast.success(t(formatted.title), toastOptions)
          break
        case NotificationTypeEnum.INFO:
          toast.info(t(formatted.title), toastOptions)
          break
        default:
          toast(t(formatted.title), toastOptions)
          break
      }
    }
  }, [userNotifications, queryClient, router, t, markAsRead, updateDashboardNotificationCount])

  const markAllAsRead = () => {
    if (!data?.items?.length) {
      return
    }
    const ids = data.items.map((item: any) => item.id)
    markAsRead(
      { params: { ids } },
      {
        onSuccess: () => {
          // Reset unread notifications count in dashboard cache to 0
          updateDashboardNotificationCount(null)
          void refetch()
        }
      }
    )
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" className="relative">
          <BellIcon className="h-4 w-4" />
          {unreadCount > 0 && (
            <Badge className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center p-0 text-xs">
              {displayCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="border-sidebar-border w-96 max-w-[calc(100vw-1rem)]">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>{t('notifications')}</span>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Badge variant="secondary">
                {unreadCount} {t('new')}
              </Badge>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" onClick={() => refetch()}>
                  <RefreshCcwIcon />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('refresh')}</TooltipContent>
            </Tooltip>
            {data?.items && data.items.length > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon" onClick={markAllAsRead} disabled={isMarkingRead}>
                    <CheckCheckIcon />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('mark_all_as_read')}</TooltipContent>
              </Tooltip>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <ScrollArea className="my-3 h-78">
          <div className="px-2">
            {isLoading ? (
              <div className="text-muted-foreground py-8 text-center text-sm">{t('loading')}</div>
            ) : data?.items && data.items.length > 0 ? (
              <div className="flex flex-col divide-y">
                {data.items.map(item => (
                  <NotificationItem
                    key={item.id}
                    item={item}
                    onNavigate={() => setOpen(false)}
                    requireDeleteConfirmation={false}
                    showCheckbox={false}
                  />
                ))}
              </div>
            ) : (
              <div className="text-muted-foreground py-8 text-center text-sm">{t('no_notifications')}</div>
            )}
          </div>
        </ScrollArea>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="justify-center text-center" onClick={() => router.push('/notifications')}>
          {t('view_all')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default NotificationsDropdown
